import { Controller, useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import styles from "./styles.module.scss";
import PrimaryButton from "../../../../../../components/common/PrimaryButton";
import TextInput from "../../../../../../components/forms/textInput";
import Modal from "../../../../../../components/common/Modal";
import Spinner from "../../../../../../components/common/Spinner";

export type AddClientInputs = {
  description: string;
};

interface IAddClientModalProps {
  modalOpen: boolean;
  loading: boolean;
  handleClose: () => void;
  handleSave: (description: string) => void;
}

/**
 * Self-service "add another API client" (design doc §9 Phase 2). Only a description is
 * collected — client_id/secret are generated server-side, and the new client always starts
 * auto-accept-consent=false regardless of the org's other clients.
 */
export default function AddClientModal({
  modalOpen,
  handleClose,
  handleSave,
  loading,
}: IAddClientModalProps) {
  const { t } = useTranslation();
  const { control, reset, handleSubmit } = useForm<AddClientInputs>({
    mode: "onChange",
    defaultValues: { description: "" },
  });

  return (
    <Modal
      visible={modalOpen}
      onCancel={() => {
        handleClose();
        reset();
      }}
      customModalClassName={styles.modal}
      customModalContainerClassName={styles.modalContainer}>
      <div className={styles.container}>
        <h1 className={styles.title}>{t("lbl_add_client_title")}</h1>
        <p className={styles.subtitle}>{t("lbl_add_client_description")}</p>

        <Controller
          name="description"
          control={control}
          render={({ field }) => (
            <TextInput
              {...field}
              placeholder={t("lbl_client_description_placeholder")}
            />
          )}
        />

        <div className={styles.submitButton}>
          <PrimaryButton
            disabled={loading}
            onClick={(data) => {
              handleSubmit((values) => handleSave(values.description))(data);
            }}>
            {!loading ? t("lbl_create_client") : <Spinner />}
          </PrimaryButton>
        </div>
      </div>
    </Modal>
  );
}
